import type { ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * Hard clock-in gate for hourly office staff (Issue #516).
 *
 * The server decides when a clock-in is required (`requireClockIn` on
 * `/time-clock/current`): hourly + not clocked in + within office work hours
 * (America/Chicago). While required, this blocks the whole app behind a
 * full-screen "clock in to start your day" screen. Non-hourly users and
 * out-of-hours sessions pass straight through. This is a punch-honesty gate,
 * not a security boundary — it fails open on load/error.
 */
interface CurrentClock {
    clockedIn: boolean;
    requireClockIn?: boolean;
}

export function ClockInGate({ children }: { children: ReactNode }) {
    const { isHourly, user, logout } = useAuth();
    const qc = useQueryClient();

    const { data } = useQuery({
        queryKey: ["time-clock", "current"],
        queryFn: () => api.get<CurrentClock>("/api/time-clock/current"),
        refetchInterval: 60_000,
        enabled: isHourly && !!user,
    });

    const clockIn = useMutation({
        mutationFn: () => api.post("/api/time-clock/in"),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["time-clock"] });
            toast.success("Clocked in — have a good day.");
        },
        onError: () => toast.error("Could not clock in. Try again."),
    });

    if (isHourly && data?.requireClockIn) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-background p-6">
                <Card className="w-full max-w-md text-center shadow-lg">
                    <CardContent className="space-y-5 px-8 py-10">
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                            <Clock className="h-7 w-7 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-xl font-black tracking-tight">Clock in to start your day</h1>
                            <p className="mt-2 text-sm text-foreground/70">Tap Clock in to open Groundwork Command.</p>
                        </div>
                        <Button
                            size="lg"
                            className="w-full"
                            disabled={clockIn.isPending}
                            onClick={() => clockIn.mutate()}
                        >
                            {clockIn.isPending ? "Clocking in…" : "Clock in"}
                        </Button>
                        <p className="text-xs text-muted-foreground">
                            Signed in as {user?.display_name ?? user?.email}. Not you?{" "}
                            <button className="underline hover:text-foreground" onClick={() => logout()}>
                                Sign out
                            </button>
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return <>{children}</>;
}
